import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from '../context/FormContext'
import { useDraft } from '../hooks/useDraft'
import { useOfflineQueue } from '../hooks/useOfflineQueue'
import { validateStep, hasErrors } from '../utils/validation'
import { buildPayload } from '../utils/exportUtils'
import api from '../utils/api'
import StepIndicator from './ui/StepIndicator'
import Button from './ui/Button'
import Alert from './ui/Alert'
import ReviewScreen from './ReviewScreen'
import Step1_Applicant    from './steps/Step1_Applicant'
import Step2_MartyrInfo   from './steps/Step2_MartyrInfo'
import Step3_Incident     from './steps/Step3_Incident'
import Step4_Witnesses    from './steps/Step4_Witnesses'
import Step5_Family       from './steps/Step5_Family'
import Step6_Verification from './steps/Step6_Verification'
import { Check, Save, Trash2, ChevronRight, ChevronLeft, FileEdit, Search } from 'lucide-react'

const TOTAL_STEPS = 6

// ─── Track search widget (header) ────────────────────────────────────────────
function TrackSearch() {
  const navigate = useNavigate()
  const [ref, setRef] = useState('')

  function handleSubmit(e) {
    e.preventDefault()
    const trimmed = ref.trim().toUpperCase()
    if (trimmed) navigate(`/track?ref=${encodeURIComponent(trimmed)}`)
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-1.5">
      <div className="relative">
        <input
          type="text"
          value={ref}
          onChange={e => setRef(e.target.value)}
          placeholder="رقم مرجعي..."
          dir="ltr"
          className="bg-primary-600 border border-primary-500 text-white placeholder-primary-300 text-xs rounded-lg px-3 py-1.5 w-36 sm:w-44 focus:outline-none focus:ring-2 focus:ring-white/30"
        />
      </div>
      <button
        type="submit"
        title="متابعة الطلب"
        className="bg-primary-600 hover:bg-primary-500 border border-primary-500 rounded-lg p-1.5 transition-colors"
      >
        <Search size={14} />
      </button>
    </form>
  )
}

function WizardInner({ notice }) {
  const {
    state,
    setStep,
    setErrors,
    setSubmittedCase,
    setSubmissionStatus,
    loadDraft: loadDraftIntoContext,
    resetForm,
  } = useForm()
  const { currentStep, formData, files, submissionStatus } = state

  const draftHook  = useDraft()
  const { isOnline, enqueue } = useOfflineQueue()
  const navigate   = useNavigate()

  const [draftStatus, setDraftStatus]   = useState('idle') // 'idle'|'prompting'|'dismissed'
  const [showReview, setShowReview]     = useState(false)
  const [submitError, setSubmitError]   = useState(null)
  const [autoSavedAt, setAutoSavedAt]   = useState(null)

  // On mount — check for existing draft
  useEffect(() => {
    if (draftHook.hasDraft()) setDraftStatus('prompting')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      draftHook.saveDraft(formData, currentStep)
      setAutoSavedAt(new Date().toLocaleTimeString('ar-SA'))
    }, 1500)
    return () => clearTimeout(timer)
  }, [formData, currentStep]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleResumeDraft = () => {
    const d = draftHook.loadDraft()
    if (d) loadDraftIntoContext(d.formData, d.step)
    setDraftStatus('dismissed')
  }

  const handleDiscardDraft = () => {
    draftHook.deleteDraft()
    resetForm()
    setDraftStatus('dismissed')
  }

  const goToStep = useCallback(
    (targetStep) => {
      if (targetStep < 1 || targetStep > TOTAL_STEPS) return
      if (targetStep > currentStep) {
        const errs = validateStep(currentStep, formData, files)
        if (hasErrors(errs)) { setErrors(errs); return }
      }
      setErrors({})
      setStep(targetStep)
    },
    [currentStep, formData, files, setStep, setErrors]
  )

  const handleNext = () => {
    const errs = validateStep(currentStep, formData, files)
    if (hasErrors(errs)) { setErrors(errs); return }
    if (currentStep === TOTAL_STEPS) {
      setShowReview(true)
    } else {
      setStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (showReview) { setShowReview(false); return }
    if (currentStep > 1) setStep(currentStep - 1)
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    const errs = validateStep(TOTAL_STEPS, formData, files)
    if (hasErrors(errs)) {
      setErrors(errs); setShowReview(false); setStep(TOTAL_STEPS); return
    }
    if (!isOnline) {
      await enqueue(buildPayload(formData), files)
      setSubmissionStatus('queued')
      draftHook.deleteDraft()
      navigate('/success', { state: { queued: true } })
      return
    }
    setSubmissionStatus('submitting')
    try {
      const fd = new FormData()
      fd.append('data', JSON.stringify(buildPayload(formData)))
      if (files.martyr_photo_upload) {
        fd.append('martyr_photo_upload', files.martyr_photo_upload, files.martyr_photo_upload.name)
      }
      for (const f of files.evidence_upload || []) {
        fd.append('evidence_upload', f, f.name)
      }
      const { data } = await api.post('submissions', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setSubmittedCase(data)
      draftHook.deleteDraft()
      navigate('/success', { state: { case: data.case, formData } })
    } catch (err) {
      setSubmitError(err.message)
      setSubmissionStatus('error')
    }
  }

  const stepComponents = {
    1: Step1_Applicant,
    2: Step2_MartyrInfo,
    3: Step3_Incident,
    4: Step4_Witnesses,
    5: Step5_Family,
    6: Step6_Verification,
  }
  const ActiveStep = stepComponents[currentStep]

  return (
    <div className="min-h-screen bg-surface" dir="rtl">
      {/* Header */}
      <header className="bg-primary-700 text-white shadow-lg no-print">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logos/logo_icon.png" alt="شعار الجمهورية العربية السورية" className="h-12 w-auto flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base lg:text-lg font-bold leading-tight">
                المنصة الوطنية لتوثيق شهداء ومفقودي الثورة السورية المباركة
              </h1>
              <p className="text-xs text-primary-200 mt-0.5">حفظاً للذاكرة، وإحقاقاً للحق</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {autoSavedAt && (
              <span className="flex-shrink-0 text-xs text-primary-300 flex items-center gap-1">
                <Check size={12} />
                حُفظ {autoSavedAt}
              </span>
            )}
            <TrackSearch />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Admin notice banner */}
        {notice && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-sm leading-relaxed">
            {notice}
          </div>
        )}
        {/* Draft prompt */}
        {draftStatus === 'prompting' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-xl shadow-sm">
            <p className="text-amber-900 font-semibold mb-3 flex items-center gap-2">
              <FileEdit size={16} className="flex-shrink-0" />
              يوجد مسوّدة محفوظة — هل تريد استئنافها؟
            </p>
            <div className="flex gap-3 flex-wrap">
              <Button onClick={handleResumeDraft} variant="primary" size="sm">استئناف المسوّدة</Button>
              <Button onClick={handleDiscardDraft} variant="ghost" size="sm">بدء من جديد</Button>
            </div>
          </div>
        )}

        {/* Submit error */}
        {submitError && (
          <div className="mb-4">
            <Alert type="error" message={submitError} onClose={() => setSubmitError(null)} />
          </div>
        )}

        {/* Step Indicator */}
        {!showReview && <StepIndicator currentStep={currentStep} onStepClick={goToStep} />}

        {/* Main content */}
        {showReview ? (
          <ReviewScreen
            formData={formData}
            files={files}
            onEdit={(step) => { setShowReview(false); setStep(step) }}
            onConfirm={handleSubmit}
            onBack={() => setShowReview(false)}
            loading={submissionStatus === 'submitting'}

          />
        ) : (
          <div className="card max-w-3xl mx-auto">
            <ActiveStep />

            {/* Draft actions */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100 flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => { draftHook.saveDraft(formData, currentStep); setAutoSavedAt(new Date().toLocaleTimeString('ar-SA')) }} className="flex items-center gap-1.5">
                <Save size={14} />
                حفظ مسوّدة
              </Button>
              {draftHook.hasDraft() && (
                <Button variant="ghost" size="sm" onClick={() => { draftHook.deleteDraft(); setAutoSavedAt(null) }} className="text-red-500 hover:text-red-600 flex items-center gap-1.5">
                  <Trash2 size={14} />
                  حذف المسوّدة
                </Button>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-200 no-print">
              <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1} className="flex items-center gap-1.5">
                <ChevronRight size={16} />
                السابق
              </Button>
              <div className="flex items-center gap-3">
                <Button variant="primary" onClick={handleNext} loading={submissionStatus === 'submitting'} className="flex items-center gap-1.5">
                  {currentStep === TOTAL_STEPS ? (
                    <>مراجعة وتقديم</>
                  ) : (
                    <>التالي <ChevronLeft size={16} /></>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="mt-12 py-6 border-t border-stone-200 text-center text-xs text-stone-400 no-print">
        <img src="/logos/logo_vertical.png" alt="" className="h-10 mx-auto mb-2 opacity-25" />
        <p>المنصة الوطنية لتوثيق شهداء ومفقودي الثورة السورية المباركة</p>
      </footer>
    </div>
  )
}

export default function MartyrForm({ notice }) {
  return (
    <FormProvider>
      <WizardInner notice={notice} />
    </FormProvider>
  )
}
